import React from "react";

const SASH_ANGLE_DEG = -18;
const SASH_ALPHA = 0.85;
const SASH_WIDTH_PCT = 128;

/** Place-based gradient for angled name sash (1 = leader). */
export function getSashGradientForPlace(place) {
  const a = SASH_ALPHA;
  if (place === 1) {
    return `linear-gradient(90deg, rgba(255,242,182,${a}), rgba(243,198,77,${a}))`;
  }
  if (place === 2) {
    return `linear-gradient(90deg, rgba(242,244,248,${a}), rgba(191,199,214,${a}))`;
  }
  if (place === 3) {
    return `linear-gradient(90deg, rgba(255,209,163,${a}), rgba(196,122,44,${a}))`;
  }
  return `linear-gradient(90deg, rgba(207,231,255,${a}), rgba(143,194,255,${a}))`;
}

function SashNameFit({ text }) {
  const containerRef = React.useRef(null);
  const textRef = React.useRef(null);
  const [fontPx, setFontPx] = React.useState(26);

  const recalc = React.useCallback(() => {
    const box = containerRef.current;
    const span = textRef.current;
    if (!box || !span) return;

    const MAX = 26;
    const MIN = 9;
    let f = MAX;

    while (f >= MIN) {
      span.style.fontSize = `${f}px`;
      // eslint-disable-next-line no-unused-expressions
      span.offsetHeight;

      const fitsH = span.scrollHeight <= box.clientHeight + 1;
      const fitsW = span.scrollWidth <= box.clientWidth + 1;
      if (fitsH && fitsW) break;
      f -= 1;
    }
    setFontPx(f);
  }, []);

  React.useLayoutEffect(() => {
    recalc();
  }, [text, recalc]);

  React.useEffect(() => {
    const box = containerRef.current;
    if (!box) return;
    const ro = new ResizeObserver(() => recalc());
    ro.observe(box);
    return () => ro.disconnect();
  }, [recalc]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        maxHeight: "3.2em",
        minHeight: "1.2em",
        lineHeight: 1.15,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        overflow: "hidden",
        padding: "2px 6px",
      }}
    >
      <span
        ref={textRef}
        style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          whiteSpace: "normal",
          wordBreak: "break-word",
          hyphens: "auto",
          fontSize: `${fontPx}px`,
          fontWeight: 900,
          letterSpacing: "0.35px",
          color: "#fff",
          textShadow:
            "0 2px 8px rgba(0,0,0,0.75), 0 1px 3px rgba(0,0,0,0.9)",
          WebkitTextStroke: "0.6px rgba(0,0,0,0.55)",
          paintOrder: "stroke fill",
        }}
      >
        {text}
      </span>
    </div>
  );
}

/**
 * Team selfie (or placeholder) with angled place-coloured name sash in the lower third.
 */
export default function TeamNameSash({
  teamName,
  place = 4,
  selfieUrl,
  alt,
  borderRadius = "2em 2em 1.1em 1.1em",
  containerStyle = {},
  showFasterBadge = false,
}) {
  const sashGradient = getSashGradientForPlace(place);
  const label = alt || `${teamName} selfie`;

  return (
    <div
      style={{
        width: "100%",
        position: "relative",
        overflow: "visible",
        borderRadius,
        background: "#333d",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...containerStyle,
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          borderRadius,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {selfieUrl ? (
          <img
            src={selfieUrl}
            alt={label}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius,
              display: "block",
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff7",
              fontSize: "6.5rem",
              background: "#222c",
              borderRadius,
            }}
          >
            👥
          </div>
        )}
      </div>

      <div
        aria-hidden={!teamName}
        style={{
          position: "absolute",
          left: "50%",
          bottom: "22%",
          width: `${SASH_WIDTH_PCT}%`,
          transform: `translate(-50%, 50%) rotate(${SASH_ANGLE_DEG}deg)`,
          transformOrigin: "center center",
          zIndex: 4,
          pointerEvents: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            background: sashGradient,
            boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
            padding: "6px 8px",
          }}
        >
          <SashNameFit text={teamName} />
        </div>
      </div>

      {showFasterBadge && (
        <div
          title="Answered faster"
          style={{
            position: "absolute",
            top: "8%",
            right: "6%",
            zIndex: 5,
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "linear-gradient(145deg, #ff8a65, #ff3b30)",
            border: "2px solid #fff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.15rem",
            lineHeight: 1,
            pointerEvents: "none",
          }}
        >
          ⚡
        </div>
      )}
    </div>
  );
}
